import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Chart from 'chart.js/auto'; // Import Chart.js
import { PlusIcon, SearchIcon, TrashIcon, PencilIcon } from 'lucide-react';
import { faPlus, faSearch, faTrash, faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import Healthcareai from '../images/Healthcare-ai.jpg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faEdit, faUserCircle, faTrashAlt, faCheckCircle,faGear, faTimes, faBars } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios'; // Import axios for making HTTP requests
import { Line } from 'react-chartjs-2';
import { useAuth } from '../components/AuthContext ';
import axiosInstance from '../components/axiosInstance';
import SereniMindlogoMain from '../images/SereniMindLogoMain.png';
import { ClipLoader } from 'react-spinners'; // Install this with 'npm install react-spinners'
import SerenimindDashboardOnboarding  from './InteractiveTour';

const NOTIFICATION_DISMISSAL_KEY = 'dashboardNotificationPromptDismissedAt';
const NOTIFICATION_PROMPT_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;
const NOTIFICATION_ALERT_COOLDOWN_MS = 12 * 60 * 60 * 1000;

const Dashboardmain = () => {
const [isActivitiesOpen, setIsActivitiesOpen] = useState(false);
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);
  const [first_name, setFirstName] = useState('');
  const [last_name, setLastName] = useState('');
  const [todos, setTodos] = useState([]);
  const [completedTodos, setCompletedTodos] = useState([]);
  const [newTodo, setNewTodo] = useState('');
  const [deadline, setDeadline] = useState('');
  const [isEditingDeadline, setIsEditingDeadline] = useState(false);
  const [goalRatio, setGoalRatio] = useState('');
  const [username, setUsername] = useState('');
  const refreshToken = localStorage.getItem('refresh_token');
  const [newGoal, setNewGoal] = useState('');
  const [goals, setGoals] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingState, setLoadingState] = useState({ add: false });
  const { token, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(false);
  const [notificationSettingsLoaded, setNotificationSettingsLoaded] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(() => (
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported'
  ));
  const [notificationPromptCooldownActive, setNotificationPromptCooldownActive] = useState(false);
  const [notificationStatusMessage, setNotificationStatusMessage] = useState('');

  const notificationSupported = typeof window !== 'undefined' && 'Notification' in window;

  const getNotificationPromptCooldownState = () => {
    if (!notificationSupported || Notification.permission !== 'default') {
      return false;
    }

    const dismissedAt = Number(localStorage.getItem(NOTIFICATION_DISMISSAL_KEY) || 0);
    return dismissedAt > 0 && (Date.now() - dismissedAt) < NOTIFICATION_PROMPT_COOLDOWN_MS;
  };

  const refreshNotificationPromptCooldown = () => {
    setNotificationPromptCooldownActive(getNotificationPromptCooldownState());
  };

  useEffect(() => {
    // Check if it's the user's first visit
    const isFirstVisit = localStorage.getItem('dashboardOnboardingComplete') !== 'true';
    if (isFirstVisit) {
      setShowOnboarding(true);
    }
  }, []);

  const handleOnboardingClose = () => {
    setShowOnboarding(false);
    localStorage.setItem('dashboardOnboardingComplete', 'true');
  };

  useEffect(() => {
    fetchGoals();
  }, []);

  useEffect(() => {
    if (notificationSupported) {
      setNotificationPermission(Notification.permission);
      refreshNotificationPromptCooldown();
    }
  }, [notificationSupported]);

  useEffect(() => {
    const fetchNotificationSettings = async () => {
      try {
        const response = await axiosInstance.get('settings/');
        setPushNotificationsEnabled(Boolean(response.data.push_notifications));
      } catch (error) {
        console.error('Error fetching notification settings:', error);
        setPushNotificationsEnabled(false);
      } finally {
        setNotificationSettingsLoaded(true);
      }
    };

    fetchNotificationSettings();
  }, []);

  // Auto-refresh urgency every minute
  useEffect(() => {
    const interval = setInterval(() => {
      // Force re-render to update urgency calculations
      setGoals(prev => [...prev]);
    }, 60000); // Refresh every 60 seconds

    return () => clearInterval(interval);
  }, []);

  // Fetch the goals (todos) from the backend
  const fetchGoals = async () => {
    try {
      const response = await axiosInstance.get('todos/', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGoals(response.data);
    } catch (error) {
      console.error('Error fetching goals:', error);
    }
  };

  // Add a new goal (todo)
  const addGoal = async () => {

    if (!newGoal.trim() || !deadline.trim()) {
      alert("Please enter a goal and set a deadline.");
      return;
    }
  
    setLoadingState({ add: true });
    
    try {
      const response = await axiosInstance.post('todos/', {
        text: newGoal,
        deadline: deadline,

      });
  
      setGoals([...goals, response.data]);
      setNewGoal('');
    } catch (error) {
      console.error('Error adding goal:', error);
      alert('Error adding goal. Please try again.');
    } finally {
      setLoadingState({ add: false });
    }
  };
  

  // Complete a goal (todo)
  const completeGoal = async (id) => {
    setLoading(true); // Show spinner
    try {
      const response = await axiosInstance.put(`todos/${id}/complete/`);
      const updatedGoals = goals.map((goal) =>
        goal.id === id ? { ...goal, completed: true } : goal
      );
      setGoals(updatedGoals);
    } catch (error) {
      console.error('Error completing goal:', error);
    }
    setTimeout(() => {
      setLoading(false);
      console.log('Loading ended');
    }, 1000); // 1 second delay
  };

  // Delete a goal (todo)
  const deleteGoal = async (id) => {
    setLoading(true); // Show spinner
    try {
      await axiosInstance.delete(`todos/${id}`);
      const updatedGoals = goals.filter((goal) => goal.id !== id);
      setGoals(updatedGoals);
    } catch (error) {
      console.error('Error deleting goal:', error);
      alert('Error deleting goal. Please try again.');
    }
    setTimeout(() => {
      setLoading(false);
      console.log('Loading ended');
    }, 1000); // 1 second delay
  };

  // Send overdue goals email notification
  const sendOverdueGoalsEmail = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post('todos/send-overdue-email/');
      
      if (response.data.enabled === false) {
        alert('⚠️ Overdue goals email notifications are disabled.\n\nTo enable them, go to Settings > Notification Settings and turn on "Overdue Goals Email Reminders".');
      } else if (response.data.count > 0) {
        alert(`✅ Email sent successfully!\n\n${response.data.count} overdue goal${response.data.count > 1 ? 's' : ''} notification sent to your email.`);
      } else {
        alert('✨ Great job! You have no overdue goals.');
      }
    } catch (error) {
      console.error('Error sending overdue goals email:', error);
      if (error.response?.data?.error) {
        alert(`Error: ${error.response.data.error}\n${error.response.data.details || ''}`);
      } else {
        alert('Failed to send overdue goals email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter goals based on search
  const filteredGoals = goals.filter((goal) =>
    goal.text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Separate completed and incomplete goals
  const completedGoals = filteredGoals.filter((goal) => goal.completed);
  const incompleteGoals = filteredGoals.filter((goal) => !goal.completed);

  // Calculate goal urgency based on deadline
  const getGoalUrgency = (deadline) => {
    if (!deadline) return { level: 'none', label: '', color: '' };
    
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffMs = deadlineDate - now;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMs < 0) {
      // Past due
      return { 
        level: 'overdue', 
        label: '⚠️ OVERDUE', 
        color: 'bg-red-100 border-red-500 text-red-800',
        badgeColor: 'bg-red-600 text-white',
        message: `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''}`
      };
    } else if (diffMinutes <= 30) {
      // Due in 30 minutes or less
      return { 
        level: 'critical', 
        label: '🔴 DUE IN 30 MIN', 
        color: 'bg-red-50 border-red-400 text-red-700',
        badgeColor: 'bg-red-500 text-white animate-pulse',
        message: `Due in ${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}!`
      };
    } else if (diffDays === 0) {
      // Due today
      return { 
        level: 'urgent', 
        label: '🟠 DUE TODAY', 
        color: 'bg-orange-50 border-orange-400 text-orange-700',
        badgeColor: 'bg-orange-500 text-white',
        message: `Due in ${diffHours} hour${diffHours !== 1 ? 's' : ''}`
      };
    } else if (diffDays === 1) {
      // Due tomorrow (1 day)
      return { 
        level: 'warning', 
        label: '🟡 DUE TOMORROW', 
        color: 'bg-yellow-50 border-yellow-400 text-yellow-800',
        badgeColor: 'bg-yellow-500 text-white',
        message: 'Due in 1 day'
      };
    } else if (diffDays <= 3) {
      // Due soon
      return { 
        level: 'upcoming', 
        label: '🔵 DUE SOON', 
        color: 'bg-blue-50 border-blue-300 text-blue-700',
        badgeColor: 'bg-blue-500 text-white',
        message: `Due in ${diffDays} days`
      };
    } else {
      // Normal
      return { 
        level: 'normal', 
        label: '', 
        color: 'bg-gray-50 border-gray-200 text-gray-800',
        badgeColor: '',
        message: `${diffDays} days remaining`
      };
    }
  };

  // Sort incomplete goals by urgency
  const sortedIncompleteGoals = [...incompleteGoals].sort((a, b) => {
    const urgencyOrder = { overdue: 0, critical: 1, urgent: 2, warning: 3, upcoming: 4, normal: 5, none: 6 };
    const urgencyA = getGoalUrgency(a.deadline).level;
    const urgencyB = getGoalUrgency(b.deadline).level;
    return urgencyOrder[urgencyA] - urgencyOrder[urgencyB];
  });

  // Get critical reminders (overdue, critical, urgent)
  const criticalReminders = sortedIncompleteGoals.filter(goal => {
    const urgency = getGoalUrgency(goal.deadline).level;
    return ['overdue', 'critical', 'urgent'].includes(urgency);
  });

  const showRelevantNotification = (permissionOverride = notificationPermission) => {
    if (!notificationSupported || permissionOverride !== 'granted' || !pushNotificationsEnabled) {
      return;
    }

    const goalToNotify = sortedIncompleteGoals.find(goal => {
      const urgencyLevel = getGoalUrgency(goal.deadline).level;
      return urgencyLevel === 'critical' || urgencyLevel === 'overdue';
    });

    if (!goalToNotify) {
      setNotificationStatusMessage('No urgent browser alerts need attention right now.');
      return;
    }

    const urgency = getGoalUrgency(goalToNotify.deadline);
    const notificationKey = `goal-${goalToNotify.id}-${urgency.level}`;
    const lastNotified = Number(localStorage.getItem(notificationKey) || 0);
    const notifiedRecently = lastNotified > 0 && (Date.now() - lastNotified) < NOTIFICATION_ALERT_COOLDOWN_MS;

    if (notifiedRecently) {
      setNotificationStatusMessage('This goal was already alerted recently, so no additional browser alert was shown.');
      return;
    }

    new Notification('Wellness Goal Alert', {
      body: `${goalToNotify.text}\n${urgency.message}`,
      icon: SereniMindlogoMain,
      tag: 'dashboard-goal-alert',
      renotify: false,
    });

    localStorage.setItem(notificationKey, String(Date.now()));
    setNotificationStatusMessage('One urgent browser alert was sent.');
  };

  const requestNotificationPermission = async () => {
    if (!notificationSupported) {
      setNotificationStatusMessage('Browser notifications are not supported in this browser.');
      return;
    }

    if (!pushNotificationsEnabled) {
      setNotificationStatusMessage('Enable push notifications in Settings before turning on browser alerts.');
      return;
    }

    if (notificationPermission === 'granted') {
      showRelevantNotification();
      return;
    }

    if (notificationPermission === 'denied') {
      setNotificationStatusMessage('Browser notifications are blocked. Update your browser site settings if you want to re-enable them.');
      return;
    }

    const cooldownActive = getNotificationPromptCooldownState();
    setNotificationPromptCooldownActive(cooldownActive);

    if (cooldownActive) {
      setNotificationStatusMessage('Notification permission was dismissed recently. Alerts will stay paused for 30 days before asking again.');
      return;
    }

    const permission = await Notification.requestPermission();
    setNotificationPermission(permission);

    if (permission === 'granted') {
      localStorage.removeItem(NOTIFICATION_DISMISSAL_KEY);
      setNotificationPromptCooldownActive(false);
      setNotificationStatusMessage('Browser alerts are enabled.');
      showRelevantNotification('granted');
      return;
    }

    localStorage.setItem(NOTIFICATION_DISMISSAL_KEY, String(Date.now()));
    setNotificationPromptCooldownActive(true);
    setNotificationStatusMessage('Notification permission was dismissed. The app will not prompt again automatically, and the button stays paused for 30 days.');
  };

  useEffect(() => {
    const fetchTodos = async () => {
      try {
        const response = await axiosInstance.get('todos/', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setTodos(response.data);
      } catch (error) {
        console.error('Error fetching todos:', error);
      }
    };

    fetchTodos();
  }, [token]);
  
  useEffect(() => {
    const calculateGoalRatio = () => {
      if (todos.length > 0) {
        const ratio = completedTodos.length / todos.length;
        setGoalRatio(ratio.toFixed(2)); // Limit to 2 decimal places
      } else {
        setGoalRatio('');
      }
    };

    calculateGoalRatio();
  }, [todos, completedTodos]);

  
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await axiosInstance.get('user-info-dashboard/', {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          },
        });
        const { username, first_name, last_name } = response.data;
        setUsername(username);
        setFirstName(first_name);
        setLastName(last_name);
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };

    fetchUserInfo();
  }, []);

  useEffect(() => {
    if (!pushNotificationsEnabled) {
      setNotificationStatusMessage('Browser alerts are turned off in your settings.');
    }
  }, [pushNotificationsEnabled]);

 
  const toggleSideNav = () => {
    setIsSideNavOpen(!isSideNavOpen);
  };  const [isEditName, setIsEditName] = useState(false);

  const [isAnonymous, setIsAnonymous] = useState(false);

  const toggleActivities = () => {
    setIsActivitiesOpen(!isActivitiesOpen);
  };

  const toggleEditName = () => {
    setIsEditName(!isEditName);
  };

  const toggleAnonymous = () => {
    setIsAnonymous(!isAnonymous);
  };
  const handleNewTodoChange = (e) => {
    setNewTodo(e.target.value);
  };
  const getUserId = async () => {
    try {
      const response = await axiosInstance.get('user-info-dashboard/', {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const { user_id } = response.data; // Assuming the user ID field is named 'user_id'
      return user_id;
    } catch (error) {
      console.error('Error fetching user ID:', error);
      return null;
    }
  };

  const addTodo = async () => {
    const userId = await getUserId(); // Wait for getUserId to fetch the user ID
  
    if (userId && newTodo.trim() !== '') {
      try {
        // Fetch the last todo to determine the next available ID
        const lastTodoResponse = await axiosInstance.get('todos/?ordering=-id&limit=1');
        const lastTodoId = lastTodoResponse.data.length > 0 ? lastTodoResponse.data[0].id : 0;
        const nextTodoId = lastTodoId + 1;
  
        const response = await axiosInstance.post('todos/', {
          id: nextTodoId,
          text: newTodo,
          deadline: deadline,
          user: userId, // Send the user ID (pk) value
        });
        setTodos([...todos, response.data]);
        setNewTodo('');
        setDeadline('');
      } catch (error) {
        console.error('Error adding todo:', error);
        // Handle other types of errors
      }finally {
        setLoadingState({ ...loadingState, add: false });
      }
    }
  };
  
  const completeTodo = async (id) => {
    try {
      const response = await axiosInstance.put(`todos/${id}/complete/`);
      const updatedTodos = todos.filter(todo => todo.id !== id); // Remove completed goal from uncompleted goals list
      const completedTodo = todos.find(todo => todo.id === id);
      setTodos(updatedTodos);
      setCompletedTodos([...completedTodos, completedTodo]); // Add completed goal to completed goals list
    } catch (error) {
      console.error('Error completing todo:', error);
      // Display a user-friendly error message
      alert('Error completing todo. Please try again later.');
    } finally {
      setLoadingState({ ...loadingState, complete: false });
    }
  };  
  
const removeTodo = async (id) => {
  try {
    const access_token = localStorage.getItem('accessToken');

    const response = await axiosInstance.delete(`todos/${id}/`, {
      headers: {
        'Authorization': `Token ${access_token}`
      }
    });

    if (response.status === 204) {
      const updatedTodos = todos.filter(todo => todo.id !== id);
      const updatedCompletedTodos = completedTodos.filter(todo => todo.id !== id);
      setTodos(updatedTodos); // Remove deleted goal from the list
      setCompletedTodos(updatedCompletedTodos);
    } else {
      throw new Error('Failed to remove todo');
    }
  } catch (error) {
    console.error('Error removing todo:', error);
    // Handle error
  } finally {
    setLoadingState({ ...loadingState, remove: false });
  }
};
  
const deleteCompletedTodo = (id) => {
  
  const updatedCompletedTodos = completedTodos.filter(todo => todo.id !== id);
  setCompletedTodos(updatedCompletedTodos);
};


  const [moodData, setMoodData] = useState({
    date: new Date().toISOString().slice(0, 10),
    mood_rating: 5,
    notes: '',
  });
  const [moodHistory, setMoodHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [moodStats, setMoodStats] = useState({ totalEntries: 0, averageRating: 0, todaysMood: null, weekAverage: 0 });
  const [loadingMood, setLoadingMood] = useState(false);
  const [savingMood, setSavingMood] = useState(false);
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedMoodRating, setSelectedMoodRating] = useState(5);

  // Mood emojis mapping
  const moodEmojis = {
    1: { emoji: '😢', label: 'Terrible', color: 'bg-red-100 border-red-500 text-red-700' },
    2: { emoji: '😟', label: 'Very Bad', color: 'bg-red-50 border-red-400 text-red-600' },
    3: { emoji: '😕', label: 'Bad', color: 'bg-orange-100 border-orange-400 text-orange-700' },
    4: { emoji: '😐', label: 'Poor', color: 'bg-orange-50 border-orange-300 text-orange-600' },
    5: { emoji: '😶', label: 'Okay', color: 'bg-yellow-100 border-yellow-400 text-yellow-700' },
    6: { emoji: '🙂', label: 'Fair', color: 'bg-yellow-50 border-yellow-300 text-yellow-600' },
    7: { emoji: '😊', label: 'Good', color: 'bg-green-100 border-green-400 text-green-700' },
    8: { emoji: '😄', label: 'Very Good', color: 'bg-green-50 border-green-500 text-green-600' },
    9: { emoji: '😁', label: 'Great', color: 'bg-blue-100 border-blue-400 text-blue-700' },
    10: { emoji: '🤩', label: 'Excellent', color: 'bg-purple-100 border-purple-500 text-purple-700' },
  };

  // Fetch mood entries on mount
  useEffect(() => {
    fetchMoodEntries();
  }, []);

  const fetchMoodEntries = async () => {
    try {
      setLoadingMood(true);
      const response = await axiosInstance.get('mood-entries/');
      setMoodHistory(response.data);
      setFilteredHistory(response.data);
    } catch (error) {
      console.error('Error fetching mood entries:', error);
    } finally {
      setLoadingMood(false);
    }
  };

  const handleInputChange = (e) => { 
    const { name, value } = e.target; 
    setMoodData({ 
      ...moodData, 
      [name]: value, 
    }); 
  };

  const handleMoodSelect = (rating) => {
    setSelectedMoodRating(rating);
    setMoodData({ ...moodData, mood_rating: rating });
  };

  const saveMoodData = async (e) => {
    e.preventDefault();
    
    try {
      setSavingMood(true);
      const response = await axiosInstance.post('mood-entries/', {
        date: moodData.date,
        mood_rating: selectedMoodRating,
        notes: moodData.notes,
      });
      
      setMoodHistory([response.data, ...moodHistory]);
      setFilteredHistory([response.data, ...filteredHistory]);
      
      // Reset form
      setMoodData({
        date: new Date().toISOString().slice(0, 10),
        mood_rating: 5,
        notes: '',
      });
      setSelectedMoodRating(5);
      
      alert('✅ Mood entry saved successfully!');
    } catch (error) {
      console.error('Error saving mood entry:', error);
      alert('❌ Failed to save mood entry. Please try again.');
    } finally {
      setSavingMood(false);
    }
  };

  useEffect(() => {
    updateMoodStats();
    applyDateFilter();
  }, [moodHistory, dateFilter]);

  const updateMoodStats = () => {
    const totalEntries = moodHistory.length;
    const totalRating = moodHistory.reduce((acc, entry) => acc + parseInt(entry.mood_rating), 0);
    const averageRating = totalEntries > 0 ? (totalRating / totalEntries).toFixed(1) : 0;
    
    // Today's mood
    const today = new Date().toISOString().slice(0, 10);
    const todayEntry = moodHistory.find(entry => entry.date === today);
    const todaysMood = todayEntry ? parseInt(todayEntry.mood_rating) : null;
    
    // Last 7 days average
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const recentEntries = moodHistory.filter(entry => new Date(entry.date) >= sevenDaysAgo);
    const weekTotal = recentEntries.reduce((acc, entry) => acc + parseInt(entry.mood_rating), 0);
    const weekAverage = recentEntries.length > 0 ? (weekTotal / recentEntries.length).toFixed(1) : 0;
    
    setMoodStats({ totalEntries, averageRating, todaysMood, weekAverage });
  };

  const applyDateFilter = () => {
    let filtered = [...moodHistory];
    const now = new Date();
    
    switch(dateFilter) {
      case '7days':
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(now.getDate() - 7);
        filtered = moodHistory.filter(entry => new Date(entry.date) >= sevenDaysAgo);
        break;
      case '30days':
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(now.getDate() - 30);
        filtered = moodHistory.filter(entry => new Date(entry.date) >= thirtyDaysAgo);
        break;
      case 'good':
        filtered = moodHistory.filter(entry => parseInt(entry.mood_rating) >= 7);
        break;
      case 'bad':
        filtered = moodHistory.filter(entry => parseInt(entry.mood_rating) <= 4);
        break;
      default:
        filtered = moodHistory;
    }
    
    setFilteredHistory(filtered);
  };

  const getMoodColor = (rating) => {
    return moodEmojis[rating]?.color || 'bg-gray-100 border-gray-300 text-gray-700';
  };

  const moodChartData = {
    labels: [...filteredHistory].reverse().map((entry) => new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
    datasets: [
      {
        label: 'Mood Rating',
        data: [...filteredHistory].reverse().map((entry) => entry.mood_rating),
        fill: true,
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        borderColor: 'rgba(34, 197, 94, 1)',
        borderWidth: 3,
        tension: 0.4,
        pointBackgroundColor: 'rgba(34, 197, 94, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
    ],
  };

  const moodChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 13 },
        callbacks: {
          label: function(context) {
            const rating = context.parsed.y;
            const emoji = moodEmojis[rating]?.emoji || '';
            const label = moodEmojis[rating]?.label || '';
            return `${emoji} ${label} (${rating}/10)`;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 10,
        ticks: {
          stepSize: 1,
          font: { size: 12 },
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        ticks: {
          font: { size: 11 },
          maxRotation: 45,
          minRotation: 45,
        },
        grid: {
          display: false,
        },
      },
    },
  };

  const deleteMood = async (id) => {
    if (!window.confirm('Are you sure you want to delete this mood entry?')) return;
    
    try {
      setLoadingMood(true);
      await axiosInstance.delete(`mood-entries/${id}/`);
      const updatedHistory = moodHistory.filter((entry) => entry.id !== id);
      setMoodHistory(updatedHistory);
      setFilteredHistory(filteredHistory.filter((entry) => entry.id !== id));
      alert('✅ Mood entry deleted successfully!');
    } catch (error) {
      console.error('Error deleting mood entry:', error);
      alert('❌ Failed to delete mood entry. Please try again.');
    } finally {
      setLoadingMood(false);
    }
  };
  return (
    <div className='flex min-h-screen bg-gray-100'>
             
    <div className={`  transform ${isSideNavOpen ? 'translate-x-0' : '-translate-x-full'} w-[280px] bg-white shadow-lg transition-transform duration-300 ease-in-out z-20 md:translate-x-0 smallscreensidenav`}>
<div className="p-6">
 <nav>
   <ul className="space-y-2">
     <li><button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded"><Link to="/sereniai" className="flex items-center font-semibold">🤖 SereniAI</Link></button></li>
     <li><button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded">          <li><Link to="/book-professionals" className="flex items-center font-semibold">📚 Book our Professionals</Link></li>
     </button></li>
     <li><button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded">
     <Link to="/activity-lounge" className="flex items-center font-semibold">🎮 Activity Lounge</Link>
     </button></li>
     <li><button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded">
     <Link to="/blog" className="flex items-center font-semibold">📝 Blog</Link>
     </button></li>
     <li><button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded">
     <Link to="/journal" className="flex items-center font-semibold">✍🏼 Your Journal</Link>
     </button></li>
     <li><button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded">
     <Link to="/community" className="flex items-center font-semibold">👥 Community</Link>
     </button></li>
     {isAdmin && (
       <li><button className="w-full text-left py-2 px-4 rounded bg-[#59844F] hover:bg-[#4b7443] text-white">
       <Link to="/admin-sereni" className="flex items-center font-semibold">⚙️ Admin Panel</Link>
       </button></li>
     )}
   </ul>
 </nav>
</div>

</div>
            
        {/* Profile and To-do List */}
        <div className="w-[900px] overflow-y-auto md:h-[560px]">
        <div className="p-6 bg-gradient-to-br from-green-50 to-blue-50 rounded-lg">
      
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center">
              <span className="mr-3 text-4xl">🎯</span>
              Wellness Goals
            </h1>
            <p className="text-gray-600 text-sm mt-1 ml-14">Track your journey to better mental health</p>
          </div>
          <div className="text-right space-y-2">
            <div className="bg-white rounded-lg shadow-sm px-4 py-2">
              <p className="text-xs text-gray-500">Progress</p>
              <p className="text-2xl font-bold text-green-600">
                {goals.length > 0 ? Math.round((completedGoals.length / goals.length) * 100) : 0}%
              </p>
            </div>
            
            {/* Notification Permission Button */}
            {notificationSupported && notificationSettingsLoaded && pushNotificationsEnabled && notificationPermission === 'default' && !notificationPromptCooldownActive && (
              <button
                onClick={requestNotificationPermission}
                className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-3 py-2 rounded-lg shadow-sm transition-colors w-full flex items-center justify-center"
                title="Enable browser notifications for urgent goals"
              >
                <span className="mr-1">🔔</span>
                Enable Alerts
              </button>
            )}
            
            {/* Notification Enabled Indicator */}
            {notificationSupported && notificationSettingsLoaded && pushNotificationsEnabled && notificationPermission === 'granted' && (
              <div className="space-y-2">
                <div className="bg-green-100 text-green-700 text-xs px-3 py-2 rounded-lg flex items-center justify-center">
                  <span className="mr-1">✅</span>
                  Alerts Enabled
                </div>
                <button
                  onClick={showRelevantNotification}
                  className="bg-white border border-green-200 text-green-700 text-xs px-3 py-2 rounded-lg shadow-sm transition-colors w-full flex items-center justify-center hover:bg-green-50"
                  title="Review the most urgent alert"
                >
                  <span className="mr-1">🔎</span>
                  Review Urgent Alert
                </button>
              </div>
            )}

            {notificationSupported && notificationSettingsLoaded && !pushNotificationsEnabled && (
              <div className="bg-gray-100 text-gray-600 text-xs px-3 py-2 rounded-lg text-center">
                Browser alerts are off in Settings.
              </div>
            )}

            {notificationSupported && notificationSettingsLoaded && pushNotificationsEnabled && notificationPermission === 'default' && notificationPromptCooldownActive && (
              <div className="bg-amber-50 text-amber-700 text-xs px-3 py-2 rounded-lg text-center">
                Alerts are paused for 30 days after the last dismissed permission prompt.
              </div>
            )}

            {notificationSupported && notificationSettingsLoaded && pushNotificationsEnabled && notificationPermission === 'denied' && (
              <div className="bg-red-50 text-red-700 text-xs px-3 py-2 rounded-lg text-center">
                Browser alerts are blocked in your browser settings.
              </div>
            )}

            {notificationStatusMessage && (
              <div className="bg-white text-gray-600 text-xs px-3 py-2 rounded-lg text-center border border-gray-200">
                {notificationStatusMessage}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Critical Reminders Section */}
      {criticalReminders.length > 0 && (
        <div className="mb-6 space-y-3">
          <div className="bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-2xl mr-3 animate-pulse">🚨</span>
              <div>
                <h3 className="font-bold text-lg">Urgent Wellness Goals!</h3>
                <p className="text-sm opacity-90">
                  You have {criticalReminders.length} goal{criticalReminders.length !== 1 ? 's' : ''} requiring immediate attention
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={sendOverdueGoalsEmail}
                className="bg-white text-red-600 hover:bg-red-50 px-4 py-2 rounded-lg font-semibold text-sm transition-colors flex items-center gap-2"
                title="Send overdue goals reminder to your email"
              >
                <span>📧</span>
                Email Reminder
              </button>
              <div className="text-3xl font-bold">{criticalReminders.length}</div>
            </div>
          </div>
          
          {/* Critical Goals List */}
          <div className="bg-white rounded-lg shadow-md border-2 border-red-200 p-4">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
              <span className="mr-2">⏰</span>
              Immediate Action Required
            </h4>
            <div className="space-y-2">
              {criticalReminders.map((goal) => {
                const urgency = getGoalUrgency(goal.deadline);
                return (
                  <div key={goal.id} className={`${urgency.color} rounded-lg p-3 border-2 flex items-center justify-between`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold px-2 py-1 rounded ${urgency.badgeColor}`}>
                          {urgency.label}
                        </span>
                        <span className="text-xs text-gray-600">{urgency.message}</span>
                      </div>
                      <p className="font-semibold text-sm">{goal.text}</p>
                      {goal.deadline && (
                        <p className="text-xs text-gray-600 mt-1">
                          Deadline: {new Date(goal.deadline).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <button 
                      onClick={() => completeGoal(goal.id)}
                      className="ml-3 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      Complete
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Add New Goal Section - Improved */}
      <div className="bg-white rounded-xl shadow-md p-5 mb-6 border-2 border-green-100">
        <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
          <span className="mr-2">➕</span>
          Create New Goal
        </h2>
        <div className="md:flex gap-3 items-end">
          <div className="flex-grow mb-3 md:mb-0">
            <label className="block text-sm font-medium text-gray-600 mb-1">Goal Description</label>
            <input
              type="text"
              placeholder="E.g., Exercise 3 times a week, meditate daily..."
              value={newGoal}
              onChange={(e) => setNewGoal(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-lg p-3 focus:border-green-500 focus:outline-none transition-colors"
            />
          </div>
          <div className="mb-3 md:mb-0">
            <label className="block text-sm font-medium text-gray-600 mb-1">Target Date</label>
            <input
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-lg p-3 focus:border-green-500 focus:outline-none transition-colors"
            />
          </div>
          <button
            onClick={addGoal}
            className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-lg hover:from-green-600 hover:to-green-700 transition-all shadow-md hover:shadow-lg font-medium flex items-center justify-center md:w-auto w-full"
            disabled={loadingState.add}
          >
            {loadingState.add ? (
              <span className="flex items-center">
                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Adding...
              </span>
            ) : (
              <span className="flex items-center">
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Add Goal
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Search Section */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="🔍 Search your goals..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border-2 border-gray-200 rounded-lg p-3 pl-4 focus:border-green-500 focus:outline-none transition-colors shadow-sm"
          />
        </div>
      </div>

      {/* Goals Grid */}
      <div className="relative">
      {loading && (
        <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-90 z-50 rounded-xl">
          <ClipLoader size={80} color={"#22c55e"} />
        </div>
      )}

      
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Incomplete Goals */}
        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-yellow-400 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-800 flex items-center">
                <span className="mr-2">📝</span>
                In Progress
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-3 py-1 rounded-full">
                  {sortedIncompleteGoals.length} {sortedIncompleteGoals.length === 1 ? 'goal' : 'goals'}
                </span>
                {sortedIncompleteGoals.filter(g => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return g.deadline && new Date(g.deadline) < today;
                }).length > 0 && (
                  <button
                    onClick={sendOverdueGoalsEmail}
                    className="bg-red-100 hover:bg-red-200 text-red-700 text-xs font-semibold px-3 py-1 rounded-full transition-colors flex items-center gap-1"
                    title="Send email reminder for overdue goals"
                  >
                    <span>📧</span>
                    Email Overdue
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {sortedIncompleteGoals.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-400 text-sm">No active goals yet</p>
              <p className="text-gray-500 text-xs mt-1">Create your first wellness goal above!</p>
            </div>
          ) : (
            <ul className="space-y-3 max-h-[400px] overflow-y-auto">
              {sortedIncompleteGoals.map((goal) => {
                const urgency = getGoalUrgency(goal.deadline);
                return (
                  <li key={goal.id} className={`${urgency.color} rounded-lg p-4 hover:opacity-90 transition-all border-2 group`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1 mr-3">
                        {/* Urgency Badge */}
                        {urgency.label && (
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs font-bold px-2 py-1 rounded ${urgency.badgeColor}`}>
                              {urgency.label}
                            </span>
                            <span className="text-xs font-medium">{urgency.message}</span>
                          </div>
                        )}
                        
                        <p className="text-gray-800 font-semibold break-words text-sm mb-1">{goal.text}</p>
                        
                        {goal.deadline && (
                          <p className="text-xs text-gray-600 flex items-center">
                            <span className="mr-1">📅</span>
                            Deadline: {new Date(goal.deadline).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            })}
                          </p>
                        )}
                      </div>
                      <button 
                        onClick={() => completeGoal(goal.id)}
                        className="text-gray-400 hover:text-green-600 transition-colors group-hover:scale-110 transform"
                        title="Mark as complete"
                      >
                        <FontAwesomeIcon icon={faCheckCircle} className="text-2xl" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Completed Goals */}
        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-800 flex items-center">
              <span className="mr-2">✅</span>
              Completed
            </h2>
            <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
              {completedGoals.length} {completedGoals.length === 1 ? 'goal' : 'goals'}
            </span>
          </div>
          
          {completedGoals.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-400 text-sm">No completed goals yet</p>
              <p className="text-gray-500 text-xs mt-1">Keep working on your wellness journey!</p>
            </div>
          ) : (
            <ul className="space-y-3 max-h-[400px] overflow-y-auto">
              {completedGoals.map((goal) => (
                <li key={goal.id} className="bg-green-50 rounded-lg p-4 hover:bg-green-100 transition-colors border border-green-200 group">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 mr-3">
                      <p className="text-gray-600 line-through break-words text-sm">{goal.text}</p>
                      {goal.deadline && (
                        <p className="text-xs text-gray-500 mt-1 flex items-center">
                          <span className="mr-1">🎉</span>
                          Completed!
                        </p>
                      )}
                    </div>
                    <button 
                      onClick={() => deleteGoal(goal.id)}
                      className="text-gray-400 hover:text-red-600 transition-colors group-hover:scale-110 transform"
                      title="Delete goal"
                    >
                      <FontAwesomeIcon icon={faTrashAlt} className="text-xl" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
    </div>
          

            {/* Mood Tracker Section - Redesigned */}
            <div className="container px-4 py-8 mx-auto">
      <div className="mb-8 text-center">
        <h2 className="text-3xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-2">
          <span>😊</span>
          Mood Tracker
          <span>📊</span>
        </h2>
        <p className="text-gray-600">Track your emotional well-being over time</p>
      </div>

      {/* Mood Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-md p-5 border-2 border-purple-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-purple-700">Today's Mood</h3>
            <span className="text-2xl">📅</span>
          </div>
          {moodStats.todaysMood !== null ? (
            <div className="flex items-center gap-2">
              <span className="text-3xl">{moodEmojis[moodStats.todaysMood]?.emoji}</span>
              <div>
                <p className="text-2xl font-bold text-purple-900">{moodStats.todaysMood}/10</p>
                <p className="text-xs text-purple-600">{moodEmojis[moodStats.todaysMood]?.label}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-purple-600 italic">No entry yet</p>
          )}
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-md p-5 border-2 border-green-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-green-700">7-Day Average</h3>
            <span className="text-2xl">📈</span>
          </div>
          <p className="text-3xl font-bold text-green-900">{moodStats.weekAverage}</p>
          <p className="text-xs text-green-600 mt-1">Last week</p>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-md p-5 border-2 border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-blue-700">All Time Avg</h3>
            <span className="text-2xl">⭐</span>
          </div>
          <p className="text-3xl font-bold text-blue-900">{moodStats.averageRating}</p>
          <p className="text-xs text-blue-600 mt-1">Overall mood</p>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl shadow-md p-5 border-2 border-orange-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-orange-700">Total Entries</h3>
            <span className="text-2xl">📝</span>
          </div>
          <p className="text-3xl font-bold text-orange-900">{moodStats.totalEntries}</p>
          <p className="text-xs text-orange-600 mt-1">Logged moods</p>
        </div>
      </div>

      {/* Add New Mood Entry */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border-2 border-green-100">
        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <span>✨</span>
          How are you feeling today?
        </h3>
        
        <form onSubmit={saveMoodData}>
          <div className="space-y-6">
            {/* Date Input */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Date</label>
              <input
                type="date"
                name="date"
                value={moodData.date}
                onChange={handleInputChange}
                required
                className="w-full border-2 border-gray-200 rounded-lg p-3 focus:border-green-500 focus:outline-none transition-colors"
              />
            </div>

            {/* Emoji Mood Selector */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Select Your Mood (1 = Worst, 10 = Best)
              </label>
              <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
                {Object.entries(moodEmojis).map(([rating, { emoji, label }]) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => handleMoodSelect(parseInt(rating))}
                    className={`p-3 rounded-lg border-2 transition-all transform hover:scale-110 ${
                      selectedMoodRating === parseInt(rating)
                        ? 'border-green-500 bg-green-50 shadow-lg scale-110'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    title={`${label} (${rating}/10)`}
                  >
                    <span className="text-3xl">{emoji}</span>
                    <div className="text-xs font-bold mt-1">{rating}</div>
                  </button>
                ))}
              </div>
              <div className="mt-3 text-center">
                <p className="text-lg font-semibold text-gray-700">
                  Selected: <span className="text-3xl mx-2">{moodEmojis[selectedMoodRating]?.emoji}</span>
                  <span className="text-green-600">{moodEmojis[selectedMoodRating]?.label}</span>
                  <span className="text-gray-500 ml-2">({selectedMoodRating}/10)</span>
                </p>
              </div>
            </div>

            {/* Notes Input */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Notes (Optional) - What's influencing your mood?
              </label>
              <textarea
                name="notes"
                value={moodData.notes}
                onChange={handleInputChange}
                maxLength="200"
                rows="3"
                placeholder="Describe what made your day good or bad, any thoughts, feelings, or events..."
                className="w-full border-2 border-gray-200 rounded-lg p-3 focus:border-green-500 focus:outline-none transition-colors resize-none"
              ></textarea>
              <div className="text-right text-xs text-gray-500 mt-1">
                {moodData.notes.length}/200 characters
              </div>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              disabled={savingMood}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-lg hover:from-green-600 hover:to-green-700 transition-all shadow-md hover:shadow-lg font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingMood ? (
                <span className="flex items-center justify-center gap-2">
                  <ClipLoader size={20} color="#fff" />
                  Saving Mood...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <span>💾</span>
                  Save Mood Entry
                </span>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Mood Trend Chart */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border-2 border-blue-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <span>📈</span>
            Mood Trend
          </h3>
          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-gray-600">Filter:</label>
            <select 
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="all">All Time</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="good">Good Moods (7+)</option>
              <option value="bad">Low Moods (≤4)</option>
            </select>
          </div>
        </div>
        
        {loadingMood ? (
          <div className="flex justify-center items-center py-20">
            <ClipLoader size={60} color="#22c55e" />
          </div>
        ) : filteredHistory.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-6xl mb-4">😊</p>
            <p className="text-gray-500 text-lg mb-2">No mood entries yet</p>
            <p className="text-gray-400 text-sm">Start tracking your mood to see trends over time!</p>
          </div>
        ) : (
          <div className="h-64">
            <Line data={moodChartData} options={moodChartOptions} />
          </div>
        )}
      </div>

      {/* Mood History */}
      <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-purple-100">
        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <span>📚</span>
          Mood History
        </h3>
        
        {loadingMood ? (
          <div className="flex justify-center items-center py-12">
            <ClipLoader size={50} color="#22c55e" />
          </div>
        ) : filteredHistory.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400">No mood entries to display</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {filteredHistory.map((entry) => {
              const moodInfo = moodEmojis[entry.mood_rating];
              return (
                <div key={entry.id} className={`${getMoodColor(entry.mood_rating)} rounded-lg p-4 border-2 transition-all hover:shadow-md`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <span className="text-4xl">{moodInfo?.emoji}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-lg">{moodInfo?.label}</span>
                          <span className="bg-white px-2 py-1 rounded-full text-xs font-semibold">
                            {entry.mood_rating}/10
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          📅 {new Date(entry.date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                        {entry.notes && (
                          <p className="text-sm text-gray-700 italic bg-white bg-opacity-50 p-2 rounded">
                            "{entry.notes}"
                          </p>
                        )}
                      </div>
                    </div>
                    <button 
                      onClick={() => deleteMood(entry.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-100 p-2 rounded-lg transition-colors ml-2"
                      title="Delete entry"
                    >
                      <FontAwesomeIcon icon={faTrashAlt} className="text-lg" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showOnboarding && (
        <SerenimindDashboardOnboarding onClose={handleOnboardingClose} />
      )}
    </div>
    </div>
    

        </div>
        
  );
};

export default Dashboardmain;
